import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { FiEdit2, FiMail, FiCalendar, FiClock, FiStar, FiLogOut, FiX } from 'react-icons/fi';
import { storageService } from '../services/storage.service';
import { updateProfile } from 'firebase/auth';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { subscriptionStatus, getStatusBadge, formatRemainingTime } = useTrialStatus();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      setError(null);
      
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        throw new Error('Image size should be less than 5MB');
      }

      // Upload avatar and get URL
      const { url } = await storageService.updateAvatar(
        user.uid, 
        file,
        user.photoURL
      );
      
      // Update user profile
      await updateProfile(user, { photoURL: url });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setError(error.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to sign out. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  return (
    <div className="profile-page">
      <button className="close-button" onClick={handleClose} aria-label="Close profile">
        <FiX size={24} />
      </button>

      <div className="profile-header">
        <h1>My Profile</h1>
        <p className="text-secondary">Personalize your experience</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="profile-content">
        <div className="profile-card main-profile">
          <div className="profile-info">
            <div className="profile-avatar">
              {user?.photoURL ? (
                <img src={user.photoURL} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  {user?.displayName?.[0] || user?.email?.[0]}
                </div>
              )}
              <input
                type="file"
                id="avatar-upload"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <label 
                htmlFor="avatar-upload" 
                className="edit-avatar-btn"
                style={{ cursor: uploadingAvatar ? 'wait' : 'pointer' }}
              >
                {uploadingAvatar ? (
                  <div className="loading-spinner" />
                ) : (
                  <FiEdit2 size={16} />
                )}
              </label>
            </div>
            <div className="profile-details">
              <div className="name-section">
                <h2>{user?.displayName || 'User'}</h2>
                <button className="edit-name-btn">
                  <FiEdit2 size={16} />
                </button>
              </div>
              <div className="profile-meta">
                <span className="meta-item">
                  <FiMail />
                  {user?.email}
                </span>
                <span className="meta-item">
                  <FiCalendar />
                  Member since {formatDate(user?.metadata?.creationTime)}
                </span>
                <span className="meta-item">
                  <FiStar />
                  {getStatusBadge()} Member
                </span>
                <span className="meta-item">
                  <FiClock />
                  {formatRemainingTime()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-card">
          <h3>Preferences</h3>
          <div className="settings-group">
            <div className="setting-item">
              <label>Daily Progress Notifications</label>
              <div className="toggle-switch">
                <input type="checkbox" id="daily-notifications" />
                <label htmlFor="daily-notifications"></label>
              </div>
            </div>
            <div className="setting-item">
              <label>Weekly Summary Email</label>
              <div className="toggle-switch">
                <input type="checkbox" id="weekly-summary" defaultChecked />
                <label htmlFor="weekly-summary"></label>
              </div>
            </div>
            <div className="setting-item">
              <label>Show Progress on Dashboard</label>
              <div className="toggle-switch">
                <input type="checkbox" id="show-progress" defaultChecked />
                <label htmlFor="show-progress"></label>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-card">
          <h3>Account Actions</h3>
          <div className="settings-group">
            <div className="setting-item">
              <label>Delete Account</label>
              <button className="btn-danger">Delete</button>
            </div>
            <div className="setting-item">
              <label>Sign Out</label>
              <button 
                className="btn-logout" 
                onClick={handleLogout}
                disabled={isLoading}
              >
                <FiLogOut />
                {isLoading ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 